import React, {
  useEffect, useCallback, useState, useMemo,
} from 'react';
import { TextField, FormHelperText } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useField, useFormikContext } from 'formik';
import debounce from 'debounce';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useId } from '../../hooks';
import useStyles from './styles';
import { QuestionCircleIcon } from '../icon/solid';
import { CustomActionCell } from '../index';

export type Option = {
  label: string;
  id: number | string;
  title?: JSX.Element;
};

type Props = {
  hideErrors?: boolean;
  options: Option[] | undefined;
  name: string;
  label: string;
  disabled?: boolean;
  disableClearable?: boolean;
  defaultValue?: number | string | null;
  onSearch?: (search: string) => void;
  loading?: boolean;
  tooltip?: string;
  callback?: (option?: Option) => void;
  isRequired?: boolean;
  selectedOption?: Option;
  isClearValueIfOptionNull?: boolean;
};

const FormSimpleAutoComplete = ({
  options = [],
  name,
  label,
  hideErrors = false,
  disabled = false,
  disableClearable = false,
  onSearch,
  loading = false,
  tooltip,
  callback,
  isRequired,
  isClearValueIfOptionNull,
  defaultValue = null,
  selectedOption,
}: Props) => {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [field, meta] = useField(name);
  const { value: fieldValue } = field;
  const { touched } = meta;
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const autoCompleteId = useId('form-input-');
  const inputId = useId('form-input-');
  const errorId = useId('input-error-');
  const [textValue, setTextValue] = useState<string>('');
  const inputRef: React.MutableRefObject<HTMLInputElement | null | undefined> = React.useRef();
  const [updateFocus, setUpdateFocus] = useState(false);
  const [startTyping, setStartTyping] = useState(false);
  const [initOptions, setInitOptions] = useState(false);
  const [initialOptionsInstalled, setInitialOptionsInstalled] = useState(false);

  const filterOptions = useMemo(() => {
    if (!onSearch && startTyping) {
      return options.filter((option) => option.label.toLowerCase().includes(textValue));
    }
    return options;
  }, [onSearch, textValue, options, startTyping]);

  const clearStates = useCallback((clearTextField: boolean = true) => {
    setFieldValue(name as never, defaultValue);
    if (clearTextField) {
      setTextValue('');
    }
    setTimeout(() => setFieldTouched(name as never, true), 5);

    if (callback) {
      if (isClearValueIfOptionNull) {
        callback(options[0]);
      } else {
        callback();
      }
    }
  }, [setFieldValue, name, setTextValue, defaultValue, callback, setFieldTouched, options, isClearValueIfOptionNull]);

  const callbackOnChangeTextField = useCallback(debounce((search: string) => {
    if (onSearch) {
      onSearch(search);
      setUpdateFocus(true);
    }
  }, 600), [onSearch, setUpdateFocus]);

  const onChangeTextField = useCallback((event) => {
    setTextValue(event.target.value || '');
    callbackOnChangeTextField(event.target.value);
    setStartTyping(true);
  }, [callbackOnChangeTextField]);

  // On change select
  const onChange = useCallback((event, option: Option) => {
    if (option) {
      // on select
      setFieldValue(name as never, option.id);
      setTimeout(() => setFieldTouched(name as never, true), 5);
      // setSelectValue(option);
      setTextValue(option.label);
      if (event.key === 'Enter') {
        event.stopPropagation();
      }
      if (callback) {
        callback(option);
      }
    } else {
      // on clear
      clearStates();
      callbackOnChangeTextField('');
    }
  }, [setFieldValue, name, setTextValue, clearStates, callbackOnChangeTextField, callback, setFieldTouched]);

  const labelRender = (
    <div>
      {label}
      {tooltip && (
        <CustomActionCell
          title={<QuestionCircleIcon className={`${classes.iconTooltip}`} />}
          classNameButtonWrapper={classes.commentButtonWrapper}
          onMouse
        >
          <p className={classes.comment}>
            {tooltip}
          </p>
        </CustomActionCell>
      )}
    </div>
  );

  const onCloseHandler = useCallback(() => {
    setOpen(false);
    setStartTyping(false);
    if (!touched) {
      setTimeout(() => setFieldTouched(name as never, true));
    }
  }, [setOpen, setFieldTouched, name, touched]);

  useEffect(() => {
    if (options && options.length > 0 && !initOptions) {
      setInitOptions(true);
      setTextValue(options.find((opt) => opt.id === fieldValue)?.label || '');
      if (options.length === 1 && !initOptions && isRequired) {
        setFieldValue(name as never, options[0].id);
        setInitialOptionsInstalled(true);
        setTextValue(options[0].label);
        if (callback) {
          callback(options[0]);
        }

        setTimeout(() => setFieldTouched(name as never, true), 5);
      }
    }
  }, [
    setInitOptions,
    options,
    initOptions,
    setFieldTouched,
    isRequired,
    fieldValue,
    setFieldValue,
    name,
    callback,
    setInitialOptionsInstalled,
  ]);

  // Set focus
  useEffect(() => {
    if (updateFocus && !loading && inputRef.current) {
      setUpdateFocus(false);
      inputRef.current.focus();
    }
  }, [options, inputRef, loading, updateFocus, setUpdateFocus]);

  useEffect(() => {
    if (selectedOption) {
      setTextValue(selectedOption.label);
      setFieldValue(name as never, selectedOption.id);
    }
  }, [selectedOption, setFieldValue, name]);

  return (
    <Autocomplete
      disableClearable={disableClearable}
      id={autoCompleteId}
      disabled={disabled || loading || initialOptionsInstalled}
      open={open && !loading}
      onOpen={() => { setOpen(true); }}
      onClose={onCloseHandler}
      options={filterOptions}
      value={textValue}
      getOptionLabel={() => textValue}
      // filterOptions={filterOptions}
      classes={{
        root: classes.root,
        input: classes.input,
        focused: classes.focused,
        popper: classes.popper,
      }}
      onChange={onChange}
      renderOption={(option:Option) => (option.title ? option.title : <>{option.label}</>)}
      renderInput={(params) => (
        <>
          <TextField
            inputRef={inputRef}
            {...params}
            value={textValue}
            id={inputId}
            label={labelRender}
            fullWidth
            onChange={onChangeTextField}
            InputLabelProps={{
              classes: {
                root: classes.labelRoot,
                focused: classes.focused,
              },
            }}
            InputProps={{
              classes: {
                input: classes.input,
                underline: classes.inputUnderline,
                focused: classes.focused,
                disabled: classes.disabled,
              },
              ...params.InputProps,
              startAdornment: loading ? <CircularProgress className={classes.loader} color="secondary" /> : null,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
          {meta.touched && meta.error && !loading && !hideErrors && (
            <FormHelperText error id={errorId}>{meta.error}</FormHelperText>
          )}
        </>
      )}
    />
  );
};

export default FormSimpleAutoComplete;
